'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import type { FC } from 'react';

import { formatPrice } from '@/lib/utils/formatPrice';
import { localeFromRouteParams } from '@/lib/i18n/localeFromRouteParams';

import styles from './PreOrderSummaryRows.module.scss';

export type MoneyDisplay = {
  amount: number;
  currency: string;
};

export interface PreOrderSummaryRowsProps {
  total?: MoneyDisplay;
  deposit: MoneyDisplay;
  balance: MoneyDisplay;
  depositPercentage: number;
  /**
   * Confirmation / order detail (deposit collected):
   * Already paid deposit → First deposit payment {pct}% → balance/final payment.
   * Cart / checkout: First deposit payment {pct}% → To pay later.
   */
  isDepositPaid?: boolean;
  /**
   * Balance row label. After final/balance payment (fully covered confirmation)
   * and pay-per-link balance charge: "Final payment". Otherwise "To pay later".
   */
  useFinalPaymentLabel?: boolean;
  /** When false, only balance/deposit rows render (footer shows total via TotalAmount). */
  showTotal?: boolean;
  showSummaryNote?: boolean;
  className?: string;
}

export const PreOrderSummaryRows: FC<PreOrderSummaryRowsProps> = ({
  total,
  deposit,
  balance,
  depositPercentage,
  isDepositPaid = false,
  useFinalPaymentLabel = false,
  showTotal = true,
  showSummaryNote = false,
  className,
}) => {
  const t = useTranslations('checkout');
  const locale = localeFromRouteParams(useParams());

  const formatAmount = (amount: number, currency: string) => formatPrice(amount, currency, locale);
  const balanceLabel = useFinalPaymentLabel
    ? t('shoppingBag.finalPayment')
    : t('shoppingBag.toPayLater');

  return (
    <div className={clsx(styles.container, className)} data-testid="pre-order-summary-rows">
      {showTotal && total ? (
        <div className={clsx(styles.row, styles.emphasized)}>
          <span>{t('cart.total')}</span>
          <span className={styles.amount}>{formatAmount(total.amount, total.currency)}</span>
        </div>
      ) : null}

      {isDepositPaid ? (
        <div className={styles.row}>
          <span>{t('shoppingBag.alreadyPaidDeposit')}</span>
          <span className={styles.amount}>{formatAmount(deposit.amount, deposit.currency)}</span>
        </div>
      ) : null}

      <div className={styles.row}>
        <span>{t('shoppingBag.firstDepositPayment', { percentage: depositPercentage })}</span>
        <span className={styles.amount}>{formatAmount(deposit.amount, deposit.currency)}</span>
      </div>

      <div className={clsx(styles.row, styles.emphasized)}>
        <span>{balanceLabel}</span>
        <span className={styles.amount}>{formatAmount(balance.amount, balance.currency)}</span>
      </div>

      {showSummaryNote ? (
        <p className={styles.note}>{t('shoppingBagSummary.preOrderNote')}</p>
      ) : null}
    </div>
  );
};
